import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Container, Drawer, Portal } from "@chakra-ui/react";
import ChatPage from "pages/chat";
import { useStore } from "contexts/store";
import { io } from "socket.io-client";
import { socket_url } from "config";
import ChatService from "services/chat";

const SOCKET_OPTIONS = {
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 8000,
    timeout: 20000,
};

const ChatContext = createContext(null);

const defaultPopupState = {
    open: false,
    seedAttachment: null,
    seedToken: 0,
};

export const ChatProvider = ({ children }) => {
    const { store } = useStore();
    const [popupState, setPopupState] = useState(defaultPopupState);
    const [unreadCount, setUnreadCount] = useState(0);
    const socketRef = useRef(null);
    const serviceRef = useRef(new ChatService());
    const isInternalChatEnabled = true;
    const externalChatURL = String(store?.web?.chat_url || "").trim();
    const hasChatEntry = true;

    const openExternalChat = () => {
        if (!externalChatURL) {
            return;
        }

        window.location = externalChatURL;
    };

    const openChat = ({ chatAttachment } = {}) => {
        setPopupState((prev) => ({
            open: true,
            seedAttachment: chatAttachment || null,
            seedToken: prev.seedToken + 1,
        }));
    };

    const closeChat = () => {
        setPopupState((prev) => ({
            ...prev,
            open: false,
        }));
    };

    const refreshUnread = async () => {
        try {
            const threads = await serviceRef.current.listThread({ page: 1, page_size: 20 });
            const current = Array.isArray(threads) ? threads[0] : null;
            setUnreadCount(Number(current?.unread_customer || 0));
        } catch (error) {
            setUnreadCount(0);
        }
    };

    useEffect(() => {
        refreshUnread();
    }, [isInternalChatEnabled]);

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) {
            return;
        }

        const socket = io(socket_url, {
            ...SOCKET_OPTIONS,
            auth: { token },
        });

        socketRef.current = socket;
        socket.on("chat:thread_updated", (thread) => {
            if (!thread?.id) {
                return;
            }
            setUnreadCount(Number(thread?.unread_customer || 0));
        });

        socket.on("reconnect", () => {
            refreshUnread();
        });

        socket.on("connect_error", () => {
            // socket.io will retry automatically
        });

        return () => {
            socket.disconnect();
            socketRef.current = null;
        };
    }, [isInternalChatEnabled]);

    const value = useMemo(() => ({
        openChat,
        closeChat,
        openExternalChat,
        hasChatEntry,
        isInternalChatEnabled,
        unreadCount,
        refreshUnread,
    }), [hasChatEntry, isInternalChatEnabled, externalChatURL, unreadCount]);

    return (
        <ChatContext.Provider value={value}>
            {children}

            <Drawer.Root
                open={popupState.open}
                onOpenChange={(e) => {
                    if (!e.open) {
                        closeChat();
                    }
                }}
                placement="bottom"
                size="full"
            >
                <Portal>
                    <Drawer.Backdrop />
                    <Drawer.Positioner>
                        <Drawer.Content>
                            <Container maxW="md" px={0} h="100dvh">
                                <ChatPage
                                    mode="overlay"
                                    onClose={closeChat}
                                    seedAttachment={popupState.seedAttachment}
                                    seedToken={popupState.seedToken}
                                />
                            </Container>
                        </Drawer.Content>
                    </Drawer.Positioner>
                </Portal>
            </Drawer.Root>
        </ChatContext.Provider>
    );
};

export const useChat = () => {
    const context = useContext(ChatContext);
    return context;
};
